import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SnackBarJs from "../SnackBar.js";
import PropTypes from "prop-types";
import {
  TextInput,
  Select,
  Checkbox,
  Textarea,
  Button,
  Group,
  useMantineColorScheme,
} from "@mantine/core";
import mgaConstants from '../../constants/index.js'
import { sanitizeText, truncateByCharacterLimit } from '../../constants/helper.js'
import { isHtmlTag, mgaRegex } from "../../constants/helper.js";

// moved inside component to allow translation via `t()`
let manufacturerListAll = [];

const COMMENTS_MAX_LENGTH = 255;

const AddAsset = ({
  getDefaultAssetsAttributeList,
  addAssetPopupStatus,
  addStatus,
  isBessSeletedDefault,
  assetsType,
  manufacturerList,
  updatedAsset,
  onCloseAssetPopUp,
  onUpdateAsset,
  getAssetDetailsAttribute,
  projectTypes,
  assetAttributes,
  addAttributeToLibrary,
  handleAsset,
  handleAssetSelection,
  handleAssetAttribute,
  asset,
  handleEditAsset,
  handleOkEditAsset,
  handleCancelUpdateAsset,
  setIsExtendedLibrary,
  inputRefs,
  currentAvailableAttributes,
  assetSubtypeList,
  selectedAssetSubtypeId,
  setAssetDetailsEx,
  setCurrentAvailableAttributes,
  isAssetTypeDisabled,
  isPortaled = false,
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const inActiveComments = t("library.inactiveComments");
  const dropDownOption = {};
  projectTypes?.forEach((item) => (dropDownOption[item] = false));
  const [loading, setLoading] = useState(true);

  const [newManufacturer, setNewManufacturer] = useState(false);
  const [assetDetails, setAssetDetails] = useState({
    assetId: 0,
    assetType: 2,
    assetName: "",
    assetSubtype: null,
    vehicleModel: "",
    manufacturer: "",
    communicationModel: "",
    version: "",
    assetInfo: "NA",
    imagePreviewUrl: null,
    imageLink: null,
    validated: true,
    comments: "",
    availability: "",
  });
  const [errorStatus, setErrorStatus] = useState({
    commentsErrorStatus: false,
    assetTypeErrorStatus: false,
    versionErrorStatus: false,
    communicationModelErrorStatus: false,
    assetSubtypeErrorStatus: false,
    manufacturerErrorStatus: false,
    assetInfoErrorStatus: false,
    availabilityErrorStatus: false,
  });

  const [snackBar, setSnackBar] = useState({ type: "", message: "" });
  const [attributeErrors, setAttributeErrors] = useState({});
  const [textFieldErrors, setTextFieldErrors] = useState({});
  const [draftAttributeError, setDraftAttributeError] = useState(false);
  const [draftAttributeAddError, setDraftAttributeAddError] = useState(false);

  const isEVCSAsset = ["EVCS", "EV_charging_station"].includes(
    assetDetails.assetName
  );
  const assetSubtypeOptions = (Array.isArray(assetSubtypeList)
    ? assetSubtypeList
    : []
  )
    .map((subtype) => {
      if (!subtype?.id || !subtype?.type) return null;
      return { label: String(subtype.type), value: String(subtype.id) };
    })
    .filter(Boolean);
  const selectableAttributeTypes = (currentAvailableAttributes || []).filter(
    (attributeType) =>
      !(assetAttributes || []).some(
        (attribute) => attribute.type === attributeType
      )
  );

  useEffect(() => {
    if (!isEVCSAsset || assetSubtypeOptions?.length === 0) return;
    const selectedSubtype = assetSubtypeList.find(
      (subtype) => String(subtype?.id) === String(selectedAssetSubtypeId)
    );
    setAssetDetails((prevState) => ({
      ...prevState,
      assetSubtype:
        selectedSubtype ||
        prevState.assetSubtype ||
        assetSubtypeList.find(
          (subtype) => String(subtype?.id) === assetSubtypeOptions[0]?.value
        ),
    }));
  }, [isEVCSAsset, assetSubtypeList, selectedAssetSubtypeId]);

  const snackBarRef = useRef(null);
  const loadAttributesForType = useCallback(async (assetTypeId, assetTypeName) => {
    const response = await getDefaultAssetsAttributeList(assetTypeName || "Generator");
    const defaultAttributeList = Array.isArray(response?.defaultAttributeList)
    ? response.defaultAttributeList
    : [];
    const allowedAttributeNames = Array.isArray(response?.allowedAttributeName)
    ? response.allowedAttributeName
    : [];
    const selectedAssetData = defaultAttributeList
      .find((data) => Number(data?.id) === Number(assetTypeId))
      ?.defaultAttributes?.map((attribute, index) => ({
        type: attribute.attributeName,
        value: attribute.attributeValue,
        id: index,
        isEdit: false,
      })) || [];
    const selectedAttributeTypes = new Set(
      selectedAssetData.map((attribute) => attribute.type)
    );
    const availableAssetData = allowedAttributeNames
      .filter((type) => !selectedAttributeTypes.has(type))
      .map((type, index) => ({
        type,
        value: "",
        id: `auto-${type}-${index}`,
        isEdit: false,
      }));

    setCurrentAvailableAttributes(allowedAttributeNames);
    setAssetDetailsEx((prevState) => ({
      ...prevState,
      assetAttributes: [...selectedAssetData, ...availableAssetData],
    }));
  }, [getDefaultAssetsAttributeList, setAssetDetailsEx, setCurrentAvailableAttributes]);

  useEffect(() => {
    if (!assetsType?.length) return;
    setLoading(false);
    if (addStatus === false) {
      setAssetDetails((prevState) => ({
        ...prevState,
        assetId: updatedAsset?.assetId,
        assetInfo: updatedAsset?.assetInfo,
        validated: updatedAsset?.validated,
        version: updatedAsset?.version,
        manufacturer: updatedAsset?.manufacturer,
        communicationModel: updatedAsset?.communicationModel,
        assetName: updatedAsset?.assetName,
        assetType: updatedAsset?.type?.id,
        assetSubtype: updatedAsset?.assetSubtype,
        vehicleModel: updatedAsset?.vehicleModel,
        comments: updatedAsset?.comments,
        imagePreviewUrl: updatedAsset?.imageLink,
        imageLink: updatedAsset?.imageLink,
        availability: updatedAsset?.availability,
      }));
    }
    let manufacturerDataList = [];
    assetsType.map((assetTypeData) => {
      let manufacturerListNew = new Set(manufacturerList[assetTypeData.type]);
      manufacturerDataList[assetTypeData.id] = Array.from(manufacturerListNew);
      return null;
    });
    manufacturerListAll = manufacturerDataList;
    const bessSelectedDefault = isBessSeletedDefault
      ? assetsType[0]?.id
      : assetDetails.assetType;
    let filteredId = assetsType.filter(
      (assetTypeData) =>
        assetTypeData.id ===
        parseInt(
          addStatus === false ? updatedAsset?.type?.id : bessSelectedDefault ,10
        )
    );

    setAssetDetails((prevState) => ({
      ...prevState,
      assetName: filteredId?.[0]?.type,
      assetType: filteredId?.[0]?.id,
    }));

    if (addStatus) {
      setCurrentAvailableAttributes([]);
      loadAttributesForType(bessSelectedDefault, filteredId?.[0]?.type);
    }
  }, [
    addStatus,
    assetsType,
    isBessSeletedDefault,
  ]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    if (["manufacturer", "communicationModel", "comments"].includes(name)) {
      setTextFieldErrors((prevErrors) => {
        if (!prevErrors[name]) return prevErrors;
        const nextErrors = { ...prevErrors };
        delete nextErrors[name];
        return nextErrors;
      });
    }
    setLoading(true);
    switch (name) {
      case "assetType":
        const filteredId = assetsType.filter(
          (assetTypeData) => assetTypeData.id === parseInt(value, 10)
        );
        handleAssetSelection({
          target: { name: "type", value: "" },
        });
        handleAsset({
          target: { name: "value", value: "" },
        });
        setDraftAttributeError(false);
        setDraftAttributeAddError(false);
        setCurrentAvailableAttributes([]);
        await loadAttributesForType(
          value,
          filteredId?.[0]?.type
        );

        setAssetDetails((prevState) => ({
          ...prevState,
          assetId: 0,
          assetType: value,
          assetName: filteredId?.[0]?.type || "",
          assetSubtype: null,
          vehicleModel: "",
          manufacturer: "",
          communicationModel: "",
          version: "",
          assetInfo: "NA",
          imagePreviewUrl: null,
          imageLink: null,
          validated: true,
          comments: "",
          availability: "",
        }));
        setErrorStatus({
          commentsErrorStatus: false,
          assetTypeErrorStatus: false,
          versionErrorStatus: false,
          communicationModelErrorStatus: false,
          assetSubtypeErrorStatus: false,
          manufacturerErrorStatus: false,
          assetInfoErrorStatus: false,
          availabilityErrorStatus: false,
        });
        setAttributeErrors({});
        setTextFieldErrors({});
        setIsExtendedLibrary(false);
        break;

      case "assetSubtype":
        setErrorStatus((prevState) => ({
          ...prevState,
          assetSubtypeErrorStatus: false,
        }));
        break;

      case "manufacturer":
        setErrorStatus((prevState) => ({
          ...prevState,
          manufacturerErrorStatus: true,
        }));
        break;

      case "communicationModel":
        setErrorStatus((prevState) => ({
          ...prevState,
          communicationModelErrorStatus: true,
        }));
        break;

      case "version":
        setErrorStatus((prevState) => ({
          ...prevState,
          versionErrorStatus: true,
        }));
        break;

      case "assetInfo":
        setErrorStatus((prevState) => ({
          ...prevState,
          assetInfoErrorStatus: true,
        }));
        break;

      case "comments":
        setErrorStatus((prevState) => ({
          ...prevState,
          commentsErrorStatus: true,
        }));
        break;

      default:
        break;
    }
    setAssetDetails((prevState) => ({
      ...prevState,

      [name]: value,
    }));
    setLoading(false);
  };

  const handleCheckBoxChange = (e) => {
    const { name, checked } = e.target;

    setAssetDetails((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
    setIsExtendedLibrary(checked);
    if (checked === false) {
      setAssetDetails((prevState) => ({
        ...prevState,
        comments: inActiveComments,
      }));
    } else {
      setAssetDetails((prevState) => ({
        ...prevState,
        comments: "",
      }));
    }
  };


  const cancelPopup = () => {
    resetValues();
    onCloseAssetPopUp();
  };

  const resetValues = () => {
    setCurrentAvailableAttributes([]);
    setTextFieldErrors({});
    setAssetDetails((prevState) => ({
      ...prevState,
      assetType: 2,
      assetName: "",
      assetSubtype: null,
      vehicleModel: "",
      manufacturer: "",
      communicationModel: "",
      version: "",
      assetInfo: "NA",
      imagePreviewUrl: null,
      imageLink: null,
      validated: true,
      comments: "",
      availability: "",
    }));
    setErrorStatus((prevState) => {
      return {
        ...prevState,
        commentsErrorStatus: false,
        assetTypeErrorStatus: false,
        versionErrorStatus: false,
        communicationModelErrorStatus: false,
        assetSubtypeErrorStatus: false,
        manufacturerErrorStatus: false,
        assetInfoErrorStatus: false,
        availabilityErrorStatus: false,
      };
    });
    setNewManufacturer(false);
  };

  const saveAsset = () => {
    setLoading(true);

    const hasIncompleteDraftAttribute =
      Boolean(asset?.type) && !String(asset?.value || "").trim();
    if (hasIncompleteDraftAttribute) {
      setDraftAttributeError(true);
      setDraftAttributeAddError(false);
      callSnackbar("error", t("library.error.attributesMandatory"));
      setLoading(false);
      return;
    }
    if (asset?.type && asset?.value) {
      setDraftAttributeAddError(true);
      setDraftAttributeError(false);
      callSnackbar("error", t("library.error.attributesMandatory"));
      setLoading(false);
      return;
    }

    const nextTextFieldErrors = {};
    ["manufacturer", "communicationModel", "comments"].forEach((field) => {
      const value = `${assetDetails[field] || ""}`;
      if (value.trim() !== "" && !mgaRegex.leadingTrailingInternalSpacesRegex.test(value)) {
        nextTextFieldErrors[field] = "spaces";
      } else if (value.trim() !== "" && isHtmlTag(value)) {
        nextTextFieldErrors[field] = "html";
      }
    });

    if (Object.keys(nextTextFieldErrors).length > 0) {
      setTextFieldErrors(nextTextFieldErrors);
      callSnackbar("error", t("library.error.invalidTextFields"));
      setLoading(false);
      return;
    }

    const nextAttributeErrors = (assetAttributes || []).reduce(
      (errors, attribute, index) => {
        const attributeKey = attribute?.id || attribute?.type || index;
        if (!attribute?.type || `${attribute?.value ?? ""}`.trim() === "") {
          errors[attributeKey] = true;
        }
        return errors;
      },
      {}
    );
    const hasEmptyAttribute = Object.keys(nextAttributeErrors).length > 0;

    if (hasEmptyAttribute) {
      setAttributeErrors(nextAttributeErrors);
      callSnackbar("error", t("library.error.attributesMandatory"));
      setLoading(false);
      return;
    }

    if (assetDetails.assetType == "") {
      callSnackbar("error", t("library.error.assetTypeMandatory"));
      setLoading(false);
    } else if (assetDetails.manufacturer.trim() === "") {
      callSnackbar("error", t("library.error.assetMakeMandatory"));
      setLoading(false);
    } else if (!isEVCSAsset && assetDetails.communicationModel.trim() === "") {
      callSnackbar("error", t("library.error.assetModelMandatory"));
      setLoading(false);
    } else if (isEVCSAsset && !assetDetails.assetSubtype) {
      setErrorStatus((prevState) => ({
        ...prevState,
        assetSubtypeErrorStatus: true,
      }));
      callSnackbar("error", t("library.vehicleCategory.required"));
      setLoading(false);
    } else if (isEVCSAsset && assetDetails.communicationModel.trim() === "") {
      setErrorStatus((prevState) => ({
        ...prevState,
        communicationModelErrorStatus: true,
      }));
      callSnackbar("error", t("library.vehicleModel.required"));
      setLoading(false);
    }  else {
      let jsonData = {
        assetId: assetDetails.assetId,
        assetInfo: assetDetails.assetInfo,
        validated: assetDetails.validated,
        version: assetDetails.version,
        manufacturer: assetDetails.manufacturer,
        communicationModel: assetDetails.communicationModel,
        assetName: assetDetails.assetName,
        assetSubtype: assetDetails.assetSubtype,
        vehicleModel: assetDetails.vehicleModel,
        type: {
          id: assetDetails.assetType,
        },
        comments: assetDetails.comments,
      };
      onUpdateAsset(jsonData);
    }
  };

  const callSnackbar = useCallback((type, message) => {
    try {
      setSnackBar({ type, message });
      snackBarRef.current.open = true;
      if( type === "error" ){
        snackBarRef.current.canClose = true;
      }
    } catch (error) {
      console.log("error in snack bar");
    }
  }, []);

  // Handler to delete an attribute and update available attributes
  const handleDeleteAttribute = (attribute) => {
    if (!attribute?.type) return;
    // Remove from assetAttributes
    setAssetDetailsEx((prev) => ({
      ...prev,
      assetAttributes: prev.assetAttributes.filter(
        (attr) => attr.type !== attribute.type
      ),
    }));
    // Optionally, update currentAvailableAttributes if needed
    setCurrentAvailableAttributes((prev) => {
      if (!prev.includes(attribute.type)) {
        return [...prev, attribute.type];
      }
      return prev;
    });
  };

  return (
    <se-dialog
      id="dialog-complex"
      className={colorScheme === "dark" ? "se-theme-dark" : ""}
      size="xlarge"
      style={isPortaled ? { zoom: "85%" } : undefined}
      open={addAssetPopupStatus}
      can-backdrop="false"
    >
      <se-dialog-header>
        {addStatus
          ? t("library.addAsset.addTitle")
          : t("library.addAsset.editTitle")}
      </se-dialog-header>

      <se-dialog-content className="se-dialog-content" style={{ backgroundColor: colorScheme === "dark" ? "#111111" : "#fff" }}>
        <se-loading loading={loading}></se-loading>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Group align="flex-end" grow>
                    <Select
                      label={t("library.assetType")}
                      withinPortal
                      required
                      disabled={isAssetTypeDisabled}
                      placeholder={t("library.selectAssetType")}
                      data={assetsType.map((assetTypeData) => ({
                        value: assetTypeData.id.toString(),
                        label:
                          assetTypeData.type === "PV_inverter"
                            ? t("library.pvInverter")
                            : assetTypeData.type === "EV_charging_station"
                            ? t("library.evChargingStation")
                            : assetTypeData.type,
                      }))}
                      value={assetDetails.assetType?.toString()}
                      onChange={(value) =>
                        handleChange({ target: { name: "assetType", value } })
                      }
                      error={
                        parseInt(assetDetails.assetType, 10) <= 0 &&
                        errorStatus.assetTypeErrorStatus
                          ? t("library.assetType.required")
                          : undefined
                      }
                      searchable
                    />
                  </Group>
                </div>
                <div className="col-md-6 mb-3">
                  {/* {addStatus ? (
                      newManufacturer ? (
                        <Group align="flex-end" grow>
                          <TextInput
                            label="Asset Manufacturer"
                            required
                            placeholder={t("library.enterNewManufacturer")}
                            maxLength={50}
                            value={assetDetails.manufacturer}
                            onChange={(e) => handleChange({ target: { name: 'manufacturer', value: e.currentTarget.value } })}
                            error={assetDetails.manufacturer.trim() === '' && errorStatus.manufacturerErrorStatus ? 'Asset Manufacturer is required.' : undefined}
                          />
                          <Button
                            variant="subtle"
                            color="green"
                            size="xs"
                            onClick={() => {
                              if (assetDetails.manufacturer.trim()) {
                                // Add new manufacturer to the list for the selected asset type
                                const typeId = parseInt(assetDetails.assetType);
                                if (manufacturerListAll[typeId] && !manufacturerListAll[typeId].includes(assetDetails.manufacturer.trim())) {
                                  manufacturerListAll[typeId].push(assetDetails.manufacturer.trim());
                                }
                                // Set the manufacturer value and switch back to dropdown
                                setAssetDetails((prevState) => ({
                                  ...prevState,
                                  manufacturer: assetDetails.manufacturer.trim(),
                                }));
                                setNewManufacturer(false);
                              }
                            }}
                            disabled={!assetDetails.manufacturer.trim()}
                            title={t("library.addManufacturer")}
                          >
                            <se-icon size="medium" color="primary">notification_ok_stroke</se-icon>
                          </Button>
                          <Button aria-label="library-cancel-new-manufacturer" variant="subtle" color="red" size="xs" onClick={enableNewManufacturer}>
                            <se-icon size="medium" color="primary">action_delete_cross</se-icon>
                          </Button>
                        </Group>
                      ) : (
                        <Select
                        withinPortal
                          label={t("library.assetManufacturer")}
                          required
                          placeholder={t("library.selectAssetManufacturer")}
                          data={manufacturerListAll[parseInt(assetDetails.assetType)]?.map((manufacturer) => ({ value: manufacturer, label: manufacturer })) || []}
                          value={assetDetails.manufacturer}
                          onChange={(value) => handleChange({ target: { name: 'manufacturer', value } })}
                          error={assetDetails.manufacturer.trim() === '' && errorStatus.manufacturerErrorStatus ? t("library.assetManufacturer.required") : undefined}
                          rightSection={
                              <Button aria-label="library-enable-new-manufacturer" variant="subtle" color="blue" size="xs" onClick={enableNewManufacturer}>
                              <se-icon size="medium" color="primary">action_editor</se-icon>
                            </Button>
                          }
                        />
                      )
                    ) :  */}

                  <TextInput
                    label={t("library.assetManufacturer")}
                    required
                    placeholder={t("library.assetManufacturer")}
                    maxLength={50}
                    value={assetDetails.manufacturer}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "manufacturer",
                          value: sanitizeText(e.currentTarget.value),
                        },
                      })
                    }
                    error={
                      textFieldErrors.manufacturer === "spaces"
                        ? t("library.error.invalidSpaces")
                        : textFieldErrors.manufacturer === "html"
                        ? t("library.error.invalidHtml")
                        : assetDetails.manufacturer.trim() === "" &&
                          errorStatus.manufacturerErrorStatus
                        ? t("library.assetManufacturer.required")
                        : undefined
                    }
                  />
                </div>
                {isEVCSAsset && (
                  <>
                      <div className="col-md-6 mb-3">
                        <Group align="flex-end" grow>
                          <Select
                            label={t("library.vehicleCategory")}
                            withinPortal
                            required
                            disabled={isAssetTypeDisabled}
                            data={assetSubtypeOptions}
                            value={assetDetails.assetSubtype?.id?.toString() || null}
                            onChange={(value) =>
                              handleChange({
                                target: {
                                  name: "assetSubtype",
                                  value: assetSubtypeList?.find(
                                    (subtype) => String(subtype?.id) === value
                                  ),
                                },
                              })
                            }
                            error={
                              !assetDetails.assetSubtype &&
                              errorStatus.assetSubtypeErrorStatus
                                ? t("library.vehicleCategory.required")
                                : undefined
                            }
                            searchable
                          />
                        </Group>
                      </div>
                  <div className="col-md-6 mb-3">
                    <TextInput
                    label={t("library.vehicleModel")}
                    required
                    maxLength={50}
                    value={assetDetails.communicationModel}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "communicationModel",
                          value: sanitizeText(e.currentTarget.value),
                        },
                      })
                    }
                    error={
                      textFieldErrors?.communicationModel === "spaces"
                        ? t("library.error.invalidSpaces")
                        : textFieldErrors?.communicationModel === "html"
                        ? t("library.error.invalidHtml")
                        : assetDetails?.communicationModel?.trim() === "" &&
                          errorStatus?.communicationModelErrorStatus
                        ? t("library.vehicleModel.required")
                        : undefined
                    }
                  />
                      </div>
                  </>
                )}
                {!isEVCSAsset && (
                  <div className="col-md-6 mb-3">
                    <TextInput
                      label={t("library.assetModel")}
                      required
                      placeholder={t("library.assetModel")}
                      maxLength={50}
                      value={assetDetails.communicationModel}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "communicationModel",
                            value: sanitizeText(e.currentTarget.value),
                          },
                        })
                      }
                      error={
                        textFieldErrors?.communicationModel === "spaces"
                          ? t("library.error.invalidSpaces")
                          : textFieldErrors?.communicationModel === "html"
                          ? t("library.error.invalidHtml")
                          : assetDetails?.communicationModel?.trim() === "" &&
                            errorStatus?.communicationModelErrorStatus
                          ? t("library.assetModel.required")
                          : undefined
                      }
                    />
                  </div>
                )}
                {/* <div className="col-md-6 mb-3">
                  <TextInput
                    label={t("library.version")}
                    placeholder={t("library.version")}
                    maxLength={10}
                    value={assetDetails.version}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "version",
                          value: e.currentTarget.value,
                        },
                      })
                    }
                  />
                </div> */}
                {/* <div className="col-md-6 mb-3">
                    <Select
                    withinPortal
                      label={t("library.communicationModel")}
                      placeholder={t("library.communicationModel")}
                      data={["NA","Hardwired", "Communicating", "Serial"].map((v) => ({
                      value: v,
                      label: t(`library.assetInfo.${v}`),
                    }))}
                    value={assetDetails.assetInfo || "NA"}
                    onChange={(value) =>
                      handleChange({
                        target: { name: "assetInfo", value: value || "NA" },
                      })
                    }
                  />
                </div> */}
                {/* <div className="col-md-6 mb-3">
                  <label className="col-md-2 col-form-label mb-3"></label>
                  <Checkbox
                    name="validated"
                    label="Active"
                    checked={assetDetails.validated}
                    onChange={handleCheckBoxChange}
                  />
                </div> */}
                <div className="col-12 mb-3">
                  <label className="col-md-2 col-form-label" style={{color: colorScheme === "dark" ? "#e0e0e0" : "#222"}}>
                    {t("library.attributes")}
                  </label>
                  <div className="col-md-12">
                    {selectableAttributeTypes.length > 0 && (
                      <Group align="flex-start" mb={8}>
                          <div className="col-md-6 pe-2">
                            <Select
                              withinPortal
                              zIndex={10000}
                              placeholder={t("library.selectAttributeType")}
                              data={selectableAttributeTypes.map((attributeType) => ({
                                  value: attributeType,
                                  label: mgaConstants?.assetLabels?.[attributeType] || attributeType,
                                }))}
                              value={asset?.type || ""}
                              onChange={(value) => {
                                setDraftAttributeError(false);
                                setDraftAttributeAddError(false);
                                handleAssetSelection({
                                  target: { name: "type", value },
                                });
                              }}                                searchable
                            />
                          </div>
                          <div className="col-sm-2">
                            <TextInput
                              placeholder={t("library.value")}
                              maxLength={10}
                              value={asset?.value || ""}
                              onChange={(e) => {
                                setDraftAttributeError(false);
                                setDraftAttributeAddError(false);
                                handleAsset({
                                  target: {
                                    name: "value",
                                    value: e.currentTarget.value,
                                  },
                                });
                              }}
                              error={
                                draftAttributeError &&
                                String(asset?.type || "").trim()
                                  ? t("library.attributeValue.required")
                                  : undefined
                              }
                            />
                          </div>
                          <div className="col-sm-3">
                            {asset?.type && asset?.value ? (
                              <div className="d-flex flex-column">
                              <Button
                                variant="subtle"
                                size="xs"
                                color="blue"
                                onClick={() => {
                                  setDraftAttributeAddError(false);
                                  addAttributeToLibrary(assetAttributes?.length);
                                }}
                                disabled={!asset?.type || !asset?.value}
                                title={t("library.addAttribute")}
                                style={{
                                  flex: "none",
                                  width: 32,
                                  minWidth: 32,
                                  height: 32,
                                  minHeight: 32,
                                  padding: 0,
                                }}
                              >
                                <se-icon size="medium" color="primary">
                                  action_add_flat
                                </se-icon>
                              </Button>
                              {draftAttributeAddError && (
                                <span className="text-danger fs-6">Please click on add button as type and value are already added</span>
                              )}
                              </div>
                            ) : (
                              // Reserve space for the add icon when disabled
                              <div
                                style={{
                                  width: 32,
                                  minWidth: 32,
                                  height: 32,
                                  minHeight: 32,
                                }}
                              />
                            )}
                          </div>
                      </Group>
                    )}
                    {assetAttributes?.length > 0 &&
                      assetAttributes?.map((attribute, index) => (
                        <Group
                          key={attribute?.id || index}
                          mb={8}
                          align="flex-start"
                        >
                            <div className="col-md-6 pe-2">
                              <TextInput
                                value={
                                  mgaConstants?.assetLabels?.[attribute?.type] ||
                                  attribute?.type
                                }
                                disabled
                                placeholder={t("library.attributeType")}
                                // style={{ flex: 1 }}
                              />
                            </div>
                            <div className="col-sm-2">
                              <TextInput
                                value={attribute?.value}
                                name="value"
                                maxLength={10}
                                onChange={(e) => {
                                  const value = e.currentTarget?.value ?? "";
                                  const attributeKey =
                                    attribute?.id || attribute?.type || index;
                                  setAttributeErrors((prevErrors) => {
                                    const nextErrors = { ...prevErrors };
                                    if (value.trim() === "") {
                                      nextErrors[attributeKey] = true;
                                    } else {
                                      delete nextErrors[attributeKey];
                                    }
                                    return nextErrors;
                                  });
                                  handleAssetAttribute(
                                    { target: { name: "value", value } },
                                    attribute
                                  );
                                }}
                                error={
                                  attributeErrors[
                                    attribute?.id || attribute?.type || index
                                  ]
                                    ? t("library.attributeValue.required")
                                    : undefined
                                }
                                disabled={false}
                                ref={(el) => (inputRefs.current[index] = el)}
                                // style={{ flex: 1 }}
                              />
                            </div>
                            <div className="col-sm-2">
                              {attribute?.type !== "type" && (
                                <Button
                                  variant="subtle"
                                  size="xs"
                                  color="red"
                                  onClick={() => handleDeleteAttribute(attribute)}
                                  title={t("library.deleteAttribute")}
                                  style={{
                                    flex: "none",
                                    width: 32,
                                    minWidth: 32,
                                    height: 32,
                                    minHeight: 32,
                                    padding: 0,
                                  }}
                                >
                                  <se-icon size="small">action_delete</se-icon>
                                </Button>
                              )}
                            </div>
                        </Group>
                      ))}
                    
                  </div>
                </div>
                <div className="col-12 mb-3">
                  <Textarea
                    label={t("library.comments")}
                    placeholder={t("library.comments")}
                    minRows={3}
                    value={assetDetails.comments}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "comments",
                          value: truncateByCharacterLimit(e.currentTarget.value, COMMENTS_MAX_LENGTH),
                        },
                      })
                    }
                    error={
                      textFieldErrors.comments === "spaces"
                        ? t("library.error.invalidSpaces")
                        : textFieldErrors.comments === "html"
                        ? t("library.error.invalidHtml")
                        : undefined
                    }
                    disabled={!assetDetails.validated}
                  /> 
                   <div className="d-flex justify-content-end mt-2">
                      <p className="m-0 small">{assetDetails.comments.length}/{COMMENTS_MAX_LENGTH} </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <SnackBarJs snackBar={snackBar} ref={snackBarRef} />
      </se-dialog-content>

      <se-dialog-footer style={{ backgroundColor: colorScheme === "dark" ? "#111111" : "#fff" }}>
        <se-button aria-label="library-add-asset-cancel" onClick={cancelPopup}>{t("common.cancel")}</se-button>

        <se-button aria-label="library-add-asset-save" color="primary" onClick={saveAsset}>
          {addStatus ? t("common.save") : t("profile.ok")}
        </se-button>
      </se-dialog-footer>
    </se-dialog>
  );
};

AddAsset.propTypes = {
  addAssetPopupStatus: PropTypes.bool,
  addStatus: PropTypes.bool,
  isBessSeletedDefault: PropTypes.bool,
  assetsType: PropTypes.array,
  manufacturerList: PropTypes.object,
  updatedAsset: PropTypes.object,
  onCloseAssetPopUp: PropTypes.func,
  onUpdateAsset: PropTypes.func,
  getAssetDetailsAttribute: PropTypes.func,
  projectTypes: PropTypes.array,
  assetAttributes: PropTypes.array,
  addAttributeToLibrary: PropTypes.func,
  handleAsset: PropTypes.func,
  handleAssetSelection: PropTypes.func,
  handleAssetAttribute: PropTypes.func,
  asset: PropTypes.object,
  handleEditAsset: PropTypes.func,
  handleOkEditAsset: PropTypes.func,
  handleCancelUpdateAsset: PropTypes.func,
  setIsExtendedLibrary: PropTypes.func,
  inputRefs: PropTypes.object,
  currentAvailableAttributes: PropTypes.array,
  assetSubtypeList: PropTypes.array,
  selectedAssetSubtypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isAssetTypeDisabled: PropTypes.bool,
  isPortaled: PropTypes.bool,
};

export default AddAsset;
